import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Modal, Backdrop, Fade } from "@mui/material";
import useTranslate from "hooks/use-translate-language";
import googleIcon from "./icons/google.svg";
import outlookIcon from "./icons/outlook.svg";
import appleIcon from "./icons/apple.svg";
import yahooIcon from "./icons/yahoo.svg";
import "./style.scss";

export const AddToCalendar = ({
  open,
  closeModal,
  title,
  description,
  startDate,
  endDate,
}) => {
  const translate = useTranslate();

  // Validate inputs
  if (!moment(startDate).isValid() || !moment(endDate).isValid()) {
    console.error("Invalid date provided to AddToCalendar", { startDate, endDate });
    return null;
  }

  if (moment(endDate).isSameOrBefore(moment(startDate))) {
    console.error("End date must be after start date", { startDate, endDate });
    return null;
  }

  // Convert to UTC for calendar services (Google, Outlook, etc. expect UTC)
  const formattedStartDate = moment(startDate).utc().format("YYYYMMDDTHHmmss[Z]");
  const formattedEndDate = moment(endDate).utc().format("YYYYMMDDTHHmmss[Z]");

  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  const startISO = moment(startDate).utc().format();
  const endISO = moment(endDate).utc().format();

  const getCalendarIcon = (icon) => {
    const icons = { google: googleIcon, outlook: outlookIcon, apple: appleIcon, yahoo: yahooIcon };
    return icons[icon];
  };

  const escapeICSValue = (value) => {
    return value
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r\n/g, '\\n')
      .replace(/\r/g, '\\n')
      .replace(/\n/g, '\\n');
  };

  // RFC 5545 §3.1: fold lines at 75 octets; continuation lines start with a single space
  const foldLine = (line) => {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(line);
    if (bytes.length <= 75) return line;

    const decoder = new TextDecoder();
    const segments = [];
    let pos = 0;

    while (pos < bytes.length) {
      const limit = pos === 0 ? 75 : 74; // continuation lines spend 1 byte on the leading space
      let end = Math.min(pos + limit, bytes.length);
      // retreat to a UTF-8 character boundary so we never split a multi-byte sequence
      while (end > pos && (bytes[end] & 0xC0) === 0x80) end--;
      segments.push(decoder.decode(bytes.slice(pos, end)));
      pos = end;
    }

    return segments.join('\r\n ');
  };

  const generateICS = () => {
    try {
      // Generate unique ID for the event
      const uid = `S3Appointment-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      // DTSTAMP is the creation timestamp in UTC
      const dtstamp = moment().utc().format("YYYYMMDDTHHmmss[Z]");

      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//My Sleep Coach//Patient Portal//EN',
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${dtstamp}`,
        `DTSTART:${formattedStartDate}`,
        `DTEND:${formattedEndDate}`,
        `SUMMARY:${escapeICSValue(title)}`,
        `DESCRIPTION:${escapeICSValue(description)}`,
        'END:VEVENT',
        'END:VCALENDAR'
      ].map(foldLine).join('\r\n');

      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'S3_Appointment.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to generate calendar file:', error);
    }
  };

  const calendarButtons = [
    {
      name: "Google Calendar",
      url: `https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${formattedStartDate}/${formattedEndDate}&text=${encodedTitle}&details=${encodedDescription}`,
      img: getCalendarIcon('google'),
      alt: "Google Calendar",
    },
    {
      name: "Outlook",
      url: `https://outlook.office.com/calendar/0/deeplink/compose?startdt=${startISO}&enddt=${endISO}&subject=${encodedTitle}&body=${encodedDescription}`,
      img: getCalendarIcon('outlook'),
      alt: "Outlook Calendar",
    },
    {
      name: "Apple Calendar",
      onClick: generateICS,
      img: getCalendarIcon('apple'),
      alt: "Apple Calendar",
    },
    {
      name: "Yahoo",
      url: `https://calendar.yahoo.com/?v=60&title=${encodedTitle}&st=${formattedStartDate}&et=${formattedEndDate}&desc=${encodedDescription}`,
      img: getCalendarIcon('yahoo'),
      alt: "Yahoo Calendar",
    },
  ];

  return (
    <Modal
      className="add-to-calendar-modal"
      open={open}
      onClose={closeModal}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
      aria-labelledby="calendar-modal-title"
    >
      <Fade in={open}>
        <div className="add-to-calendar-modal-container" role="dialog" aria-modal="true">
          <div className="modal-header">
            <h3 id="calendar-modal-title">{translate("Add event to calendar")}</h3>
            <button className="close-button" onClick={closeModal} aria-label={translate("close")}>
              &times;
            </button>
          </div>

          <div className="modal-body">
            <div className="calendar-buttons-grid">
              {calendarButtons.map((button) =>
                button.onClick ? (
                  <button
                    key={button.name}
                    className="calendar-button"
                    onClick={() => {
                      button.onClick();
                      closeModal();
                    }}
                    type="button"
                    aria-label={`${translate("Add event to calendar")} - ${button.name}`}
                  >
                    <img src={button.img} alt="" aria-hidden="true" />
                    <span>{button.name}</span>
                  </button>
                ) : (
                  <a
                    key={button.name}
                    href={button.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="calendar-button"
                    onClick={closeModal}
                    aria-label={`${translate("Add event to calendar")} - ${button.name} (${translate("opens in new tab")})`}
                  >
                    <img src={button.img} alt="" aria-hidden="true" />
                    <span>{button.name}</span>
                  </a>
                )
              )}
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

AddToCalendar.propTypes = {
  open: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
};
